import {
  GAME_DATA,
  type MorningReport,
  type TakenSquishy,
  type WsEventMessage,
} from '@heartpatch/shared';
import { firesLine, wildLine, type ReportLine } from './night-text.js';
import { joinNames } from './show-timeline.js';

/** The Heart Snack, Heartdust's use (owner decision 2026-10-06): what it's called and costs. */
const SNACK = GAME_DATA.careActions.find(
  (a) => a.outsideDailyCare === true && a.cost?.['heartdust'] !== undefined,
);

// The morning report's words (design doc §14, style guide §1, §9): kid-gentle,
// short, and every squishy taken to the Hollow is followed by "you can rescue
// them!". Pure, so the wording is unit-tested and scanned for avoided words.

export const HOLLOW_TEXT = {
  visitedOne: 'The Hollow Man visited last night…',
  visitedMany: 'The Hollow Man came by while you were away…',
  passedBy: 'The Hollow Man passed by last night…',
  taken: (name: string) => `He took ${name} to the Hollow. You can rescue them!`,
  home: (name: string) => `He took ${name} to the Hollow, but they're home again!`,
  safe: 'Everyone stayed safe and cozy. Nice planning!',
  /** Squishies were out in the dark, but he took nobody (first-night grace, or a last friend). */
  spared: 'He came by, but took nobody this time.',
  ok: 'Okay!',
  rescueNamed: (name: string) => `Rescue ${name}!`,
  open: 'Hollow',
  sheetTitle: 'In the Hollow',
  sheetIntro: 'Win a showdown with the shadows to bring them home!',
  reward: (n: number) => `Your first rescue today earns ${String(n)} Heartdust.`,
  /** What Heartdust is for (the Heart Snack), under the reward line. */
  saveUp: SNACK ? `Save ${String(SNACK.cost?.['heartdust'] ?? 0)} for a ${SNACK.name}!` : '',
  noReward: 'No more Heartdust today, but they still come home!',
  rescue: 'Rescue!',
  empty: 'Everyone is safe at home!',
  back: 'Back',
  devNightfall: 'Night falls (dev)',
  devNight: (night: string, taken: number) =>
    taken === 0 ? `Night fell (${night}). Nobody was taken.` : `Night fell (${night}).`,
  /** A gatherer or guard would spend tonight on dark land (home is always safe, owner decisions 2026-10-07). */
  fireHint: 'A friend sleeps out in the dark. Light a fire there!',
  /** My home fire packed up when the Heart Seed began keeping home safe (#202). */
  packedTitle: 'Good morning!',
  packed: [
    'Your Heart Seed keeps home safe now!',
    'Your home fire packed up, and everything came back.',
    'Build fires on your land! 🔥',
  ],
  /** My home Training Grounds packed up and moved out to homesteads (#277, owner decision 4). */
  packedGrounds: ['Your Training Grounds packed up and moved out!', 'Build one on a homestead 🏡.'],
  /** Tiles still crowded packed up when one building a tile went into the database (#295). */
  packedCrowded: [
    'Some buildings were squeezed onto one tile,',
    'so we packed them up and gave you everything back.',
    'Build again! 🏡',
  ],
  /** The tidy-up when one building a tile came in (#295). */
  tidyUp: 'Tidy-up time! Each tile holds one building now.',
  movedHome: (name: string) => `Your ${name} moved to a roomier home tile.`,
  movedOut: (name: string) => `Your ${name} moved out to a homestead 🏡.`,
  packedUp: (names: string) => `Your ${names} packed up, and everything came back:`,
  stopped: (names: string) => `${names} stopped gathering where a building stands now.`,
  /** Trick-or-treaters came to my home yesterday (#515, owner answer 4). */
  trickOrTreaters: (n: number) =>
    n === 1 ? 'You had 1 trick-or-treater!' : `You had ${String(n)} trick-or-treaters!`,
  trickOrTreatThanks: (n: number) => `They left you ${String(n)} Heartdust to say thanks.`,
  /** Peeks out beside the Adventure handle while a friend is in the Hollow. */
  news: 'A friend is in the Hollow!',
  mystery: 'a squishy friend',
  waiting: ', waiting in the Hollow',
} as const;

/**
 * Reports worth telling (someone taken, kept safe, or left out and spared)
 * newer than `seenNight`, newest first. A night with nobody of mine there
 * says nothing.
 */
export function unseenReports(
  reports: readonly MorningReport[],
  seenNight: string | null,
): MorningReport[] {
  return reports.filter(
    (r) =>
      (seenNight === null || r.night > seenNight) &&
      (r.taken.length > 0 || r.sheltered > 0 || r.exposed > 0 || r.reclaimed.length > 0),
  );
}

/**
 * The report card's title and lines for these nights (newest first), each
 * with its little picture (#277, mockup screen 5): what the fires did, the
 * land that went wild, then who he took. `fireHint` is the status's own ("a
 * gatherer or guard of mine would spend tonight on dark land"), so a night
 * he let them be asks for a fire only while one would.
 */
export function reportText(
  reports: readonly MorningReport[],
  nameOf: (taken: TakenSquishy) => string,
  fireHint = true,
): { title: string; lines: ReportLine[] } {
  const taken = reports.flatMap((r) => r.taken);
  const wild = wildLine(reports);
  const title =
    taken.length === 0 && !wild
      ? HOLLOW_TEXT.passedBy
      : reports.length > 1
        ? HOLLOW_TEXT.visitedMany
        : HOLLOW_TEXT.visitedOne;
  const fires = firesLine(reports);
  const lines: ReportLine[] = [...(fires ? [fires] : []), ...(wild ? [wild] : [])];
  // Who he took, in one line each for those still waiting and those home again.
  const waiting = taken.filter((t) => t.inHollow).map(nameOf);
  const home = taken.filter((t) => !t.inHollow).map(nameOf);
  if (waiting.length > 0) lines.push({ icon: '🌫️', text: HOLLOW_TEXT.taken(joinNames(waiting)) });
  if (home.length > 0) lines.push({ icon: '🏡', text: HOLLOW_TEXT.home(joinNames(home)) });
  if (taken.length === 0 && !wild) {
    // Nobody taken: either everyone was sheltered, or some were out in the
    // dark and he let them be (first-night grace), which is the moment to
    // say "light a fire" (owner decision 2026-10-03).
    if (reports.some((r) => r.taken.length === 0 && r.exposed > 0)) {
      lines.push(
        { icon: '🌙', text: HOLLOW_TEXT.spared },
        ...(fireHint ? [{ icon: '🔥', text: HOLLOW_TEXT.fireHint }] : []),
      );
    } else {
      lines.push({ icon: '💖', text: HOLLOW_TEXT.safe });
    }
  }
  return { title, lines };
}

/**
 * Events that can change who of mine spends tonight in the dark: a fire lit,
 * put out or reaching further, or a gatherer or guard sent out or brought in
 * (owner decision 2026-10-07: guards need a fire's light too).
 */
const NIGHT_EVENTS = new Set([
  'building.placed',
  'building.fueled',
  'building.upgraded',
  'building.removed',
  'squishy.assigned',
  'defenders.changed',
]);

/** Something of mine changed that the fire hint depends on: it asks again. */
export function changesMyNight(
  event: Pick<WsEventMessage, 'type' | 'data'>,
  userId: string,
): boolean {
  return NIGHT_EVENTS.has(event.type) && event.data['userId'] === userId;
}

const BUILDING_NAMES = new Map(GAME_DATA.buildings.map((b) => [b.id, b.name]));
const HOME_ONLY = new Set(
  GAME_DATA.buildings.filter((b) => b.placement === 'home').map((b) => b.id),
);

/** What the one-time tidy-up note (#295) says: what moved, what packed up, who stopped gathering. */
export interface TileUseNoteLike {
  readonly moved: readonly { readonly buildingId: string }[];
  readonly packed: readonly { readonly buildingId: string }[];
  readonly stoppedGatherers: readonly { readonly name: string }[];
}

/**
 * The tidy-up's lines for the morning report (#295): one line a building
 * moved (home ones to another home tile, the rest out to a homestead), one
 * for everything that packed up (its refund follows, said by the screen),
 * and one for squishies that stopped gathering. Empty when nothing changed.
 */
export function tileUseLines(note: TileUseNoteLike): string[] {
  const name = (id: string) => BUILDING_NAMES.get(id) ?? 'building';
  const lines = note.moved.map((m) =>
    HOME_ONLY.has(m.buildingId)
      ? HOLLOW_TEXT.movedHome(name(m.buildingId))
      : HOLLOW_TEXT.movedOut(name(m.buildingId)),
  );
  if (note.packed.length > 0) {
    lines.push(HOLLOW_TEXT.packedUp(joinNames(note.packed.map((p) => name(p.buildingId)))));
  }
  if (note.stoppedGatherers.length > 0) {
    lines.push(HOLLOW_TEXT.stopped(joinNames(note.stoppedGatherers.map((g) => g.name))));
  }
  return lines.length > 0 ? [HOLLOW_TEXT.tidyUp, ...lines] : [];
}

/**
 * The host's trick-or-treat note for the morning report (#515, the approved
 * screen): how many came yesterday, and the thank-you Heartdust they left.
 */
export function trickOrTreatLines(note: { visitors: number; heartdust: number }): ReportLine[] {
  return [
    { icon: '🎃', text: HOLLOW_TEXT.trickOrTreaters(note.visitors) },
    ...(note.heartdust > 0
      ? [{ icon: '💗', text: HOLLOW_TEXT.trickOrTreatThanks(note.heartdust) }]
      : []),
  ];
}
